import moment from "moment";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { Hit } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import {
  getBadgePropsForAuthStatus,
  MedicalPaOrdersAuthStatus,
} from "../../../../enums/medicalPaOrdersAuthStatus";
import { MedicalPaOrderStatus } from "../../../../enums/medicalPaOrderStatus";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../../utils/stringModifications";
import { getOrderIcons } from "../../medicalPaOrdersAlgolia/utils/getOrderIcons";
import { getDocStatusCellData } from "./docStatus";

/**
 * Maps a master auth status string to its corresponding medical PA order status type
 * for status log display purposes.
 *
 * @param status - Master auth status value
 * @returns MedicalPaOrderStatus.PENDING, MedicalPaOrderStatus.QUERY, or "others"
 */
const determineStatusLogType = (status: string) => {
  if (status === MedicalPaOrdersAuthStatus.Pending) {
    return MedicalPaOrderStatus.PENDING;
  } else if (status === MedicalPaOrdersAuthStatus.Query) {
    return MedicalPaOrderStatus.QUERY;
  }
  return "others";
};

/**
 * Transform Algolia Hit records to table-ready data format
 *
 * Converts Algolia search hits (simplified medical order objects) into
 * formatted table data ready for display in CustomTable component.
 *
 * Transformations include:
 * - Patient name formatting (first + last name, capitalized)
 * - Date formatting to MM/DD/YYYY
 * - Plan details (payer name + member ID)
 * - Auth status badges with colors
 * - EV/Doc write-back status
 * - Left border indicators for pending/hold orders with color coding
 * - Order processing and review status icons
 * - Provider name formatting
 * - Instructions pass-through, shaped as `{ displayText, tooltipText }`
 *   for the `STRING_WITH_CUSTOM_TOOLTIP` cell renderer (both values use
 *   the same `instructions` text — the cell truncates visually and shows
 *   the full text on hover)
 * - Location pass-through as a plain string
 *
 * @param {Hit[]} algoliaRecords - Array of Algolia Hit objects
 * @param {AuthStatusOptionModel[]} authStatusOptions - Auth status options for badge configuration
 * @returns {GenericAuthStatusTableData[]} Array of formatted table records
 *
 * @example
 * const tableData = generateGenericAuthStatusTableDataFromAlgoliaRecords(
 *   algoliaHits,
 *   authStatusOptions
 * );
 */
export const generateGenericAuthStatusTableDataFromAlgoliaRecords = (
  algoliaRecords: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
): GenericAuthStatusTableData[] => {
  return algoliaRecords.map((algoliaRecord) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      algoliaRecord?.auth_status,
    );

    return {
      [GenericAuthStatusTableColumnKeys.DOC_STATUS]: getDocStatusCellData(
        algoliaRecord.fax_jcode_statuses,
        algoliaRecord.fax_retrieval,
      ),
      [GenericAuthStatusTableColumnKeys.PATIENT_DETAILS_WITH_LEFT_BORDER]: {
        mainText: capitalizeString(
          generateFullName(
            algoliaRecord?.first_name ?? "",
            "",
            algoliaRecord?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${algoliaRecord?.patient_id ?? ""}`,
        shouldShowLeftBorder: leftBorderShouldShow(algoliaRecord),
        leftBorderColor: getleftBorderColorforPatientDetails(algoliaRecord),
      },
      [GenericAuthStatusTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            algoliaRecord?.first_name ?? "",
            "",
            algoliaRecord?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${algoliaRecord?.patient_id ?? ""}`,
      },
      [GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          algoliaRecord?.date_of_service_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [GenericAuthStatusTableColumnKeys.PLAN_DETAILS]: {
        mainText: algoliaRecord?.primary_payer_name ?? "",
        secondaryText: algoliaRecord?.primary_member_id ?? "",
      },
      [GenericAuthStatusTableColumnKeys.MEDICATION]:
        algoliaRecord?.regimen_name ?? "",
      [GenericAuthStatusTableColumnKeys.APPOINTMENT_TYPE]:
        algoliaRecord?.regimen_name ?? "",
      [GenericAuthStatusTableColumnKeys.ASSIGNEE]:
        capitalizeString(algoliaRecord?.assigned_to_name ?? "") ?? "--",
      [GenericAuthStatusTableColumnKeys.ROW_DATA]:
        JSON.stringify(algoliaRecord),
      [GenericAuthStatusTableColumnKeys.ID]: algoliaRecord?.id ?? "",
      [GenericAuthStatusTableColumnKeys.CREATED_AT]: formatDateToUTCWithFormat(
        algoliaRecord?.created_at_iso ?? "",
        "MM/DD/YYYY",
        "--",
      ),
      [GenericAuthStatusTableColumnKeys.DATE_OF_WORK]:
        algoliaRecord?.date_of_work_iso
          ? formatDateToUTCWithFormat(
              algoliaRecord?.date_of_work_iso ?? "",
              "MM/DD/YYYY",
              "--",
            )
          : "--",
      [GenericAuthStatusTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
      },
      [GenericAuthStatusTableColumnKeys.LAST_CHECKED]:
        algoliaRecord?.date_of_work_iso
          ? formatDateToUTCWithFormat(
              algoliaRecord.date_of_work_iso,
              "MM/DD/YYYY",
              "--",
            )
          : "--",
      [GenericAuthStatusTableColumnKeys.HOLD_UNTIL]:
        algoliaRecord?.date_of_hold_until_iso
          ? formatDateToUTCWithFormat(
              algoliaRecord.date_of_hold_until_iso,
              "MM/DD/YYYY",
              "--",
            )
          : "--",
      [GenericAuthStatusTableColumnKeys.BO_VALUE]: {
        text: algoliaRecord?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [GenericAuthStatusTableColumnKeys.ESTIMATE_VALUE]: {
        text: algoliaRecord?.estimate_value ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [GenericAuthStatusTableColumnKeys.RPA_STATUS]: {
        status:
          algoliaRecord?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        id: algoliaRecord?.id ?? "",
        shouldPropagate: true,
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },
      [GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          algoliaRecord?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        filePaths: [],
        docList: [],
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
      },
      [GenericAuthStatusTableColumnKeys.PROVIDER]: rotateLastSegmentFirst(
        algoliaRecord?.practitioner_name ?? "",
      ),
      [GenericAuthStatusTableColumnKeys.SIDE_MODAL_ICON]: {
        id: algoliaRecord?.id ?? "",
        status: determineStatusLogType(algoliaRecord?.master_auth_status ?? ""),
        borderColor: algoliaRecord?.notes_color ?? "",
        isNotesPresent: algoliaRecord?.notes_present ?? false,
        patientId: algoliaRecord?.patient_id ?? "",
      },
      [GenericAuthStatusTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          algoliaRecord?.medical_order_status as MedicalOrderStatus | undefined,
          algoliaRecord?.medical_order_review_status as
            MedicalOrderReviewStatus | undefined,
          algoliaRecord?.master_auth_status === "query" &&
            algoliaRecord?.ai_agent_type === "agent",
        ),
        order_id: algoliaRecord?.id ?? "",
      },
      [GenericAuthStatusTableColumnKeys.INSTRUCTIONS]: {
        displayText: algoliaRecord?.instructions ?? "",
        tooltipText: algoliaRecord?.instructions ?? "",
      },
      [GenericAuthStatusTableColumnKeys.LOCATION]:
        algoliaRecord?.location ?? "",
    };
  });
};

/**
 * Determine if left border indicator should be shown for patient details
 *
 * Left border is shown only for orders with "Pending" or "Hold" auth status
 * to visually highlight orders that require attention.
 *
 * @param {Hit} algoliaRecord - Algolia Hit record
 * @returns {boolean} True if left border should be displayed
 *
 * @example
 * const showBorder = leftBorderShouldShow(algoliaRecord);
 * // Returns true for pending/hold orders, false otherwise
 */
export const leftBorderShouldShow = (algoliaRecord: Hit) => {
  if (
    algoliaRecord?.master_auth_status === MedicalPaOrdersAuthStatus.Pending ||
    algoliaRecord?.master_auth_status === MedicalPaOrdersAuthStatus.Hold
  ) {
    return true;
  }
  return false;
};

/**
 * Calculate left border color based on time since last action
 *
 * Color coding indicates urgency based on hours since last checked/hold until date:
 * - Orange (#EF6C00): 24-48 hours
 * - Red (#CD0202): 48+ hours
 * - Transparent: < 24 hours or not applicable
 *
 * For Pending orders: Uses date_of_work (last checked date)
 * For Hold orders: Uses date_of_hold_until
 *
 * @param {Hit} algoliaRecord - Algolia Hit record
 * @returns {string} Hex color code for left border
 *
 * @example
 * const borderColor = getleftBorderColorforPatientDetails(algoliaRecord);
 * // Returns "#EF6C00", "#CD0202", or "transparent"
 */
export const getleftBorderColorforPatientDetails = (algoliaRecord: Hit) => {
  if (
    algoliaRecord?.date_of_work_iso &&
    algoliaRecord?.master_auth_status === MedicalPaOrdersAuthStatus.Pending
  ) {
    const lastCheckedTime = moment(algoliaRecord.date_of_work_iso);
    const now = moment();
    const hoursSinceLastChecked = now.diff(lastCheckedTime, "hours");

    if (hoursSinceLastChecked >= 24 && hoursSinceLastChecked < 48) {
      return "#EF6C00";
    } else if (hoursSinceLastChecked >= 48) {
      return "#CD0202";
    }
  } else if (
    algoliaRecord?.date_of_hold_until_iso &&
    algoliaRecord?.master_auth_status === MedicalPaOrdersAuthStatus.Hold
  ) {
    const lastCheckedTime = moment(algoliaRecord.date_of_hold_until_iso);
    const now = moment();
    const hoursSinceLastChecked = now.diff(lastCheckedTime, "hours");

    if (hoursSinceLastChecked >= 24 && hoursSinceLastChecked < 48) {
      return "#EF6C00";
    } else if (hoursSinceLastChecked >= 48) {
      return "#CD0202";
    }
  }
  return "transparent";
};
